import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ToolChip } from "./tool-chip";
import { ConfirmationCard, type RespondMutationLike } from "./confirmation-card";
import type {
  AssistantMessage,
  ChatError,
  ConfirmRequestEvent,
  LiveToolEvent,
  ToolCallRecord,
} from "@/hooks/use-assistant";
import { cn } from "@/lib/utils";

interface MessageListProps {
  history: AssistantMessage[] | undefined;
  historyLoading: boolean;
  optimisticUserMessage: string | null;
  streamText: string;
  toolEvents: LiveToolEvent[];
  pendingConfirms: ConfirmRequestEvent[];
  status: "idle" | "streaming" | "error";
  error: ChatError | null;
  onRetry: () => void;
  respond: RespondMutationLike;
  brandSlug: string;
}

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-[13px] text-primary-foreground whitespace-pre-wrap break-words">
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ content, streaming }: { content: string; streaming?: boolean }) {
  if (!content && !streaming) return null;
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-[13px] text-foreground whitespace-pre-wrap break-words">
        {content}
        {streaming && (
          <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-foreground/70 align-middle" />
        )}
      </div>
    </div>
  );
}

/** Persisted ASSISTANT row tool records → chips / confirmation cards. */
function ToolCallRows({
  messageId,
  calls,
  respond,
}: {
  messageId: string;
  calls: ToolCallRecord[];
  respond: RespondMutationLike;
}) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      {calls.map((call) => {
        if (
          call.status === "pending_confirmation" ||
          call.status === "executing" ||
          call.status === "confirmed" ||
          call.status === "dismissed" ||
          call.status === "expired"
        ) {
          return (
            <ConfirmationCard
              key={call.toolUseId}
              messageId={messageId}
              confirmationId={call.toolUseId}
              verb={call.confirm?.verb ?? "Run"}
              object={call.confirm?.object ?? call.label}
              consequence={call.confirm?.consequence ?? ""}
              respond={respond}
              initialStatus={
                call.status === "pending_confirmation" || call.status === "executing"
                  ? "pending"
                  : call.status
              }
              initialSummary={call.summary}
            />
          );
        }
        return (
          <ToolChip
            key={call.toolUseId}
            label={call.label}
            status={call.status === "error" ? "error" : "ok"}
            summary={call.summary}
          />
        );
      })}
    </div>
  );
}

export function MessageList({
  history,
  historyLoading,
  optimisticUserMessage,
  streamText,
  toolEvents,
  pendingConfirms,
  status,
  error,
  onRetry,
  respond,
  brandSlug,
}: MessageListProps) {
  const { t } = useTranslation("assistant");
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [history?.length, streamText, toolEvents.length, status]);

  const isBudget = error?.code === "BUDGET_EXCEEDED";
  const isDisabled = error?.code === "ASSISTANT_DISABLED";

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
      {historyLoading && (
        <p className="text-[12px] text-muted-foreground text-center py-6">{t("loading")}</p>
      )}
      {!historyLoading && !history?.length && !optimisticUserMessage && (
        <div className="text-center py-10 space-y-1">
          <p className="text-sm font-medium">{t("emptyState.heading")}</p>
          <p className="text-[12px] text-muted-foreground">
            {t("emptyState.body")}
          </p>
        </div>
      )}

      {history?.map((m) => {
        if (m.role === "USER") return <UserBubble key={m.id} content={m.content} />;
        if (m.role === "ASSISTANT") {
          const calls = Array.isArray(m.toolCalls) ? m.toolCalls : [];
          return (
            <div key={m.id} className="space-y-1.5">
              {calls.length > 0 && (
                <ToolCallRows messageId={m.id} calls={calls} respond={respond} />
              )}
              <AssistantBubble content={m.content} />
            </div>
          );
        }
        return null; // TOOL rows fold into the ASSISTANT row's chips
      })}

      {/* live stream tail */}
      {optimisticUserMessage && <UserBubble content={optimisticUserMessage} />}
      {toolEvents.length > 0 && (
        <div className="flex flex-col items-start gap-1.5">
          {toolEvents.map((tv) => (
            <ToolChip key={tv.toolUseId} label={tv.label} status={tv.status} summary={tv.summary} />
          ))}
        </div>
      )}
      {(streamText || status === "streaming") && (
        <AssistantBubble content={streamText} streaming={status === "streaming"} />
      )}
      {pendingConfirms.map((c) => (
        <ConfirmationCard
          key={c.confirmationId}
          messageId={c.messageId}
          confirmationId={c.confirmationId}
          verb={c.verb}
          object={c.object}
          consequence={c.consequence}
          respond={respond}
        />
      ))}

      {status === "error" && error && (
        <div
          className={cn(
            "rounded-lg border px-3 py-2.5 text-[12px] space-y-1.5",
            "border-destructive/40 text-foreground",
          )}
        >
          {isBudget ? (
            <>
              <p>{t("errors.budget.message")}</p>
              <Link
                to={`/${brandSlug}/settings/ai-usage`}
                className="underline underline-offset-2 text-[12px]"
              >
                {t("errors.budget.settingsLink")}
              </Link>
            </>
          ) : isDisabled ? (
            <p>{t("errors.disabled")}</p>
          ) : (
            <>
              <p>{t("errors.generic")}</p>
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[12px]"
                onClick={onRetry}
              >
                {t("errors.retry")}
              </Button>
            </>
          )}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
